import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, googleProvider, signInWithPopup, firestore } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, setDoc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import Cookies from "js-cookie";
import { ToastContainer, toast } from "react-toastify";
import { GoogleLoginButton } from "../DatabaseAPI/Api";
import "react-toastify/dist/ReactToastify.css";
import { generateJWT } from "../APIs/generateJWT";
import { useTheme } from "@mui/material";
import { Box, Typography, Paper, CircularProgress } from "@mui/material";

const GoogleLoginSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Preserve redirect target from query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlRedirect = params.get("redirect");
    if (urlRedirect) {
      // store decoded path (/publicdetails/ID)
      sessionStorage.setItem("redirect", decodeURIComponent(urlRedirect));
    }
  }, [location.search]);

  // After auth state resolves, send to stored redirect
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const token = Cookies.get("jwtToken");
      const redirectPath = sessionStorage.getItem("redirect");

      if (user && token && redirectPath && !isLoggingIn) {
        // remove used redirect
        sessionStorage.removeItem("redirect");
        navigate(redirectPath, { replace: true });
      }
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, [navigate, isLoggingIn]);

  const storeUserProfile = async (user) => {
    // Query UserProfileMaster by email
    const q = query(
      collection(firestore, "UserProfileMaster"),
      where("email", "==", user.email)
    );
    const snap = await getDocs(q);
    return !snap.empty;
  };

  const getUserRoleAndTenant = async (userEmail) => {
    // Query UserProfileMaster by email and return the role and tenantID
    const q = query(
      collection(firestore, "UserProfileMaster"),
      where("email", "==", userEmail)
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      const docData = snap.docs[0].data();
      let role = (docData.role || "user").toLowerCase().trim();
      // Normalize common variants
      if (["superadmin", "super-admin", "super_admin"].includes(role)) role = "superadmin";
      else if (["admin", "administrator"].includes(role)) role = "admin";
      else if (["user", "enduser", "end-user"].includes(role)) role = "user";
      const tenantID = docData.tenantID || "";
      const displayName = docData.displayName || docData.name || userEmail.split('@')[0];
      return { role, tenantID, displayName };
    }
    return { role: null, tenantID: null, displayName: null };
  };

  const handleGoogleLogin = async () => {
    try {
      setIsLoggingIn(true);
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      // Only allow login if user is registered in UserProfileMaster
      const isRegistered = await storeUserProfile(user);
      if (!isRegistered) {
        toast.error("You are not registered in the system. Please contact your administrator for access.");
        setIsLoggingIn(false);
        return;
      }

      // Fetch and store user role, tenantID, and displayName (normalized)
      const { role, tenantID, displayName } = await getUserRoleAndTenant(user.email);
      const isSuperAdmin = role === "superadmin";
      const isAdmin = role === "admin" || isSuperAdmin;
      sessionStorage.setItem("userRole", role);
      sessionStorage.setItem("isSuperAdmin", isSuperAdmin ? "true" : "false");
      sessionStorage.setItem("isAdmin", isAdmin ? "true" : "false");
      if (!isSuperAdmin) {
        sessionStorage.setItem("tenantID", tenantID);
      } else {
        sessionStorage.removeItem("tenantID"); // Ensure no tenantID for superadmin
      }
      // Debug log
      console.log(`[Login] User role: ${role}, isSuperAdmin: ${isSuperAdmin}, isAdmin: ${isAdmin}, tenantID: ${isSuperAdmin ? 'N/A' : tenantID}`);

      let token = "dummy-token";
      if (process.env.REACT_APP_ENV === "production") {
        const jwt = await generateJWT(user.email, tenantID, role);
        token = jwt.token;
      }
      // Set both cookie and sessionStorage for NavBar
      Cookies.set("jwtToken", token, { path: "/" });
      sessionStorage.setItem("jwtToken", token);
      sessionStorage.setItem("userName", displayName);

      // Navigate to the saved redirect or fallback
      const finalRedirect = sessionStorage.getItem("redirect") || "/dashboard";
      // clear redirect after reading
      sessionStorage.removeItem("redirect");
      navigate(finalRedirect, { replace: true });
    } catch (err) {
      console.error("[Login] Error during Google login:", err);
      toast.error("Login failed.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  if (!isAuthReady) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Initializing login...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', p: 2 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="primary" sx={{ mb: 3 }}>
          Google Login
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Sign in with your Google account to access Assetrack.
        </Typography>

        <GoogleLoginButton onClick={handleGoogleLogin} disabled={isLoggingIn} />
        
        {isLoggingIn && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography variant="body2">Logging in...</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default GoogleLoginSection;
